/*
	Компактынй выпадающий список для использования внутри админ панели
*/
import { CircleLoader } from "../../circle-loader/CircleLoader";
import { LineLoader } from "../../line-loader/LineLoader";
import "./compact-select.scss";
import gsap from "gsap";
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

type SelectItem = {
	key: string;
	node: React.ReactNode;
};

type CompactSelectProps = {
	items: SelectItem[];
	onSelect?: (key: string) => void;
	/* Якщо передано — показує обраний ключ з батьківського стану (наприклад Redux) */
	value?: string;
	/** Якщо true — у відкритому списку жоден пункт не підсвічується як active. */
	suppressListActiveHighlight?: boolean;
	className?: string;
	isLoading?: boolean;
	usePortal?: boolean;
	disabled?: boolean;
};

export const CompactSelect: React.FC<CompactSelectProps> = ({
	items,
	onSelect,
	value,
	suppressListActiveHighlight = false,
	className,
	isLoading,
	usePortal = false,
	disabled = false,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const listRef = useRef<HTMLUListElement | null>(null);
	const listTransformOriginRef = useRef<"top left" | "bottom left">("top left");
	const [listKeepMounted, setListKeepMounted] = useState(false);
	const [open, setOpen] = useState(false);
	const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
	// selectedKey — по умолчанию первый элемент
	const [selectedKey, setSelectedKey] = useState<string | null>(
		items && items.length ? items[0].key : null,
	);

	useEffect(() => {
		if (!items?.length) {
			setSelectedKey(null);
			return;
		}
		if (value !== undefined && value !== "") {
			const match = items.find((i) => i.key === value);
			setSelectedKey(match ? value : items[0].key);
			return;
		}
		setSelectedKey((prev) =>
			prev && items.some((i) => i.key === prev) ? prev : items[0].key,
		);
	}, [items, value]);

	const updatePortalPosition = useCallback(() => {
		if (!containerRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const MARGIN = 6;
		const MAX_LIST_HEIGHT = 220;

		const spaceBelow = viewportHeight - rect.bottom - MARGIN;
		const spaceAbove = rect.top - MARGIN;

		if (spaceBelow >= 120) {
			listTransformOriginRef.current = "top left";
			setPortalStyle({
				position: "fixed",
				top: rect.bottom + MARGIN,
				bottom: "auto",
				left: rect.left,
				minWidth: rect.width,
				maxHeight: Math.min(MAX_LIST_HEIGHT, spaceBelow),
				zIndex: 2000,
			});
		} else {
			listTransformOriginRef.current = "bottom left";
			setPortalStyle({
				position: "fixed",
				top: "auto",
				bottom: viewportHeight - rect.top + MARGIN,
				left: rect.left,
				minWidth: rect.width,
				maxHeight: Math.min(MAX_LIST_HEIGHT, spaceAbove),
				zIndex: 2000,
			});
		}
	}, []);

	const showList = open || listKeepMounted;

	useLayoutEffect(() => {
		if (open) {
			setListKeepMounted(true);
		}
	}, [open]);

	useLayoutEffect(() => {
		if (!showList) return undefined;

		if (usePortal && open && containerRef.current) {
			updatePortalPosition();
		}

		const el = listRef.current;
		if (!el) return undefined;

		const origin = usePortal ? listTransformOriginRef.current : "top center";

		if (open) {
			gsap.killTweensOf(el);
			gsap.fromTo(
				el,
				{ opacity: 0, scale: 0.92 },
				{
					opacity: 1,
					scale: 1,
					duration: 0.28,
					ease: "back.out(1.35)",
					transformOrigin: origin,
				},
			);
			return () => {
				gsap.killTweensOf(el);
			};
		}

		gsap.killTweensOf(el);
		const tw = gsap.to(el, {
			opacity: 0,
			scale: 0.96,
			duration: 0.1,
			ease: "power2.in",
			transformOrigin: origin,
			onComplete: () => setListKeepMounted(false),
		});

		return () => {
			tw.kill();
		};
	}, [open, showList, usePortal, updatePortalPosition]);

	useEffect(() => {
		if (!usePortal || !open) return;
		updatePortalPosition();
		window.addEventListener("scroll", updatePortalPosition, true);
		window.addEventListener("resize", updatePortalPosition);
		return () => {
			window.removeEventListener("scroll", updatePortalPosition, true);
			window.removeEventListener("resize", updatePortalPosition);
		};
	}, [usePortal, open, updatePortalPosition]);

	useEffect(() => {
		const onDocClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (listRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	const handleToggle = () => setOpen((s) => !s);

	const handleSelect = (key: string) => {
		setSelectedKey(key);
		setOpen(false);
		if (onSelect) onSelect(key);
	};

	const selectedItem = items.find((i) => i.key === selectedKey) || items[0];

	const list = showList ? (
		<ul
			ref={listRef}
			className='compact-select__list'
			role='listbox'
			style={usePortal ? portalStyle : undefined}>
			{items.map((it) => {
				const isActive =
					!suppressListActiveHighlight && it.key === selectedKey;
				return (
					<li
						key={it.key}
						role='option'
						aria-selected={isActive}
						className={`compact-select__item ${
							isActive ? "compact-select__item--active" : ""
						}`}
						onClick={() => handleSelect(it.key)}>
						{it.node}
					</li>
				);
			})}
		</ul>
	) : null;

	return (
		<div
			ref={containerRef}
			className={[
				"compact-select",
				open ? "compact-select--open" : "",
				disabled ? "compact-select--disabled" : "",
				usePortal ? "compact-select--portal" : "",
				className || "",
			]
				.join(" ")
				.trim()}>
			<button
				type='button'
				className='compact-select__control'
				aria-haspopup='listbox'
				aria-expanded={open}
				onClick={handleToggle}
				disabled={disabled}>
				{isLoading ? (
					<div className='compact-select__loader'>
						<LineLoader />
					</div>
				) : (
					<>
						<span className='compact-select__value'>{selectedItem?.node}</span>
						<span className='compact-select__arrow' aria-hidden>
							<img
								src='/img/icons/triangle-arrow-cropped.png'
								alt=''
								width={8}
								height={5}
							/>
						</span>
					</>
				)}
			</button>

			{usePortal ? createPortal(list, document.body) : list}
		</div>
	);
};
